import dayjs from 'dayjs'
import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap'
import { RouteParams } from '../../../api/globalTypes'
import inDepthAnalysis from '../../../api/inDepthAnalysis/hooks'
import { DataTableLayout, useDataTable, useTableState } from '../../TanstackTable'
import BadgeRiskAssessment from '../BadgeRiskAssessment/BadgeRiskAssessment'
import ContactsConnectedToCompany from './ContactsConnectedToCompany/ContactsConnectedToCompany'
import { InDepthAnalysisListProps } from './InDepthAnalysisList.types'
import InDepthAnalysisPdfModal from './InDepthAnalysisPdfModal/InDepthAnalysisPdfModal'
import { useTableColumns } from './useTableColumns'

const InDepthAnalysisList = (props: InDepthAnalysisListProps) => {
    const { formId, contactType } = props
    const { id } = useParams<RouteParams>()
    const [printModalOpen, setPrintModalOpen] = useState(false)
    const formSubmissionId = useRef('')

    const tableState = useTableState()

    const { isLoading: fetchingForms, data: formData } = inDepthAnalysis.GetFormsSubmission.useQuery(id, tableState.params)
    const { isLoading: fetchingPdf, data: pdfData, isError } = inDepthAnalysis.GetAnalysisPdf.useQuery(formSubmissionId.current)

    const { formList, lastFormSubmission } = useMemo(() => {
        const items = formData?.items ?? []
        const last = items.length ? [...items].sort((a, b) => b.id - a.id)[0] : undefined
        return {
            formList: items.filter((item) => item.id !== last?.id),
            lastFormSubmission: last,
        }
    }, [formData?.items])

    const handlePrint = (id?: number) => {
        formSubmissionId.current = id?.toString() ?? ''
        setPrintModalOpen(!printModalOpen)
    }

    const handlePrintModal = () => {
        setPrintModalOpen(!printModalOpen)
    }

    const formUrl = (id: string) => {
        return `/${contactType ? 'companies' : 'contacts'}/${id}/indepthanalysis/createEdit?formId=${formId}`
    }

    const columns = useTableColumns(handlePrint, formUrl)

    const { table } = useDataTable({
        ...tableState,
        columns,
        data: formList,
        pageCount: formData?.pagination?.totalPages ?? 0,
    })

    return (
        <>
            <Row>
                <Col sm={12} xxl={4}>
                    <Card>
                        <CardHeader>
                            <h5 className="mb-0 me-4">Trenutna dubinska analiza</h5>
                        </CardHeader>
                        <CardBody>
                            {lastFormSubmission && (
                                <>
                                    <Row className={`align-items-start mt-3`}>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-medium">Id: </span>
                                        </Col>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-light">{lastFormSubmission?.id}</span>
                                        </Col>
                                    </Row>
                                    <Row className={`align-items-start mt-3`}>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-medium">Agent: </span>
                                        </Col>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-light">
                                                {lastFormSubmission?.agent.firstName} {lastFormSubmission?.agent.lastName}
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row className={`align-items-start mt-3`}>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-medium">Procjena rizika:</span>
                                        </Col>
                                        <Col sm={6}>
                                            <BadgeRiskAssessment riskAssessment={lastFormSubmission?.riskAssessment} />
                                        </Col>
                                    </Row>
                                    <Row className={`align-items-start mt-3`}>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-medium">Datum kreiranja:</span>
                                        </Col>
                                        <Col sm={6}>
                                            <span className="flex-grow-1 fw-light">
                                                {dayjs(lastFormSubmission?.created).format('DD.MM.YYYY., HH:mm:ss')}
                                            </span>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <div className="d-flex flex-grow-1 flex-wrap mt-4">
                                <Button tag={Link as React.ElementType} to={() => formUrl(id)} color="add" className="btn-label mt-2">
                                    <i className="mdi mdi-plus align-bottom label-icon align-middle fs-16 me-2"></i>
                                    Dodaj
                                </Button>
                                {lastFormSubmission && (
                                    <>
                                        <Button onClick={() => handlePrint(lastFormSubmission?.id)} color="warning" className="btn-label mx-2 mt-2">
                                            <i className="mdi mdi-file-document-multiple-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                                            Ispis
                                        </Button>
                                        <Button
                                            tag={Link as React.ElementType}
                                            to={formUrl(id) + `&edit=true&id=${lastFormSubmission?.id}`}
                                            color="info"
                                            className="btn-label mt-2"
                                        >
                                            <i className="mdi mdi-pencil-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                                            Izmijeni
                                        </Button>
                                    </>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col sm={12} xxl={8}>
                    <Card>
                        <CardHeader>
                            <h5 className="mb-0 me-4">Povijest izmjena</h5>
                        </CardHeader>
                        <CardBody>
                            <DataTableLayout
                                table={table}
                                isLoading={fetchingForms}
                                totalItems={formData?.pagination?.totalItems}
                                theadClass="dmn-table-header"
                            />
                        </CardBody>
                    </Card>
                    {contactType && <ContactsConnectedToCompany />}
                </Col>
            </Row>
            {printModalOpen && (
                <InDepthAnalysisPdfModal
                    toggle={handlePrintModal}
                    iframeSrc={pdfData}
                    isFetching={fetchingPdf}
                    isError={isError}
                    isOpen={printModalOpen}
                />
            )}
        </>
    )
}

export default InDepthAnalysisList
